import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, Building2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { PageLoader } from '@shared/components/PageLoader';
import { connectsAPI } from '@/utils/supabase/client';
import type { ConnectsCompany } from '@/types/connects';
import { formatDistanceToNow } from 'date-fns';

export function CompaniesList() {
  const navigate = useNavigate();
  const [companies, setCompanies] = useState<ConnectsCompany[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = (q?: string) => {
    setLoading(true);
    connectsAPI.getCompanies({ q, limit: 100 })
      .then(({ companies: c, total: t }) => { setCompanies(c); setTotal(t); })
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  useEffect(() => {
    const timer = setTimeout(() => load(search.length >= 2 ? search : undefined), 300);
    return () => clearTimeout(timer);
  }, [search]);

  return (
    <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6 overflow-auto">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2 font-serif">
            <Building2 className="h-6 w-6" />
            Companies
          </h1>
          <p className="text-sm text-muted-foreground mt-1">{total} companies tracked</p>
        </div>
        <div className="relative w-full sm:w-64">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search companies..."
            className="pl-9 w-full"
          />
        </div>
      </div>

      {loading ? <PageLoader /> : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
          {companies.map((co) => (
            <Card
              key={co.id}
              className="cursor-pointer hover:shadow-md transition-shadow"
              onClick={() => navigate(`/connects/companies/${co.id}`)}
            >
              <CardContent className="p-4">
                <div className="flex items-start gap-3">
                  <div className="h-9 w-9 rounded-full bg-muted flex items-center justify-center shrink-0">
                    <Building2 className="h-4 w-4 text-muted-foreground" />
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{co.name}</p>
                    {co.industry && <p className="text-xs text-muted-foreground truncate">{co.industry}</p>}
                    {co.domain && <p className="text-xs text-muted-foreground truncate">{co.domain}</p>}
                    <div className="flex items-center gap-2 mt-2 flex-wrap">
                      {co.is_client && <Badge className="text-xs">Client</Badge>}
                      <Badge variant="secondary" className="text-xs">{co.mention_count} mentions</Badge>
                      <span className="text-xs text-muted-foreground">
                        {formatDistanceToNow(new Date(co.last_mentioned_at), { addSuffix: true })}
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
          {!loading && companies.length === 0 && (
            <div className="col-span-full text-center py-12 text-sm text-muted-foreground">
              No companies found{search ? ` for "${search}"` : ' yet'}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
